import { Icon } from '../icon';
import { Link } from '../buttons';
import React, { FunctionComponent, useEffect, useState } from 'react';
import CheckCircle from 'src/shared/icons/check-circle.svg';
import Circle from 'src/shared/icons/circle.svg';
import ListCheck from 'src/shared/icons/clipboard-check-solid.svg';
import Close from 'src/shared/icons/times-solid.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { T } from './helpers';

const ButtonTodoList = styled.button`
    background-color: ${theme.app.backgroundColor};
    border: 1px solid black;
    color: black;
    border-radius: 9999px;
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0;
    margin-bottom: 20px;
    font: ${theme.app.font};
    &:hover {
        background-color: black;
        color: ${theme.app.backgroundColor};
    }
`;

const ContainerToDoList = styled.div`
    position: fixed;
    width: 295px;
    z-index: 31;
    display: flex;
    flex-direction: column;
    padding: 24px;
    left: 0px;
    bottom: -600px;

    background: ${theme.app.backgroundColor};
    border: 1px solid #dbe2e6;
    box-shadow:
        0px 0px 4px rgba(0, 0, 0, 0.1),
        0px 8px 40px rgba(0, 0, 0, 0.2);
    border-radius: 0px 8px 0px 0px;
    transition: bottom 0.3s linear;

    &.open {
        bottom: 75px;
        box-shadow: rgba(0, 0, 0, 0.4) 0px 0px 110px 3000px;
    }
`;

const Title = styled.label`
    font-weight: 700;
    font-size: 16px;
`;

const SubTitle = styled.label`
    font-weight: 400;
    font-size: 16px;
    margin: 24px 0 20px;
`;

const NumberStepsRemaining = styled.div`
    font-weight: 400;
    font-size: 12px;
`;

const TicksSteps = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
    column-gap: 4px;
    margin: 15px 0 5px;
`;

const Tick = styled.div<{ $completed: boolean }>`
    display: block;
    border-radius: 4px;
    height: 4px;
    background-color: ${(props) => (props.$completed ? theme.app.secondary : theme.app.backgroundColor)};
`;

const CloseButton = styled.a`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    cursor: pointer;
    font-size: 24px;
    color: ${theme.app.primary};
`;

const Steps = styled.ul`
    padding: 0;
    list-style-type: none;
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 20px;
    li {
        display: grid;
        grid-template-columns: 20px 1fr;
        column-gap: 25px;
        label {
            font-size: 15px;
        }

        ${Link} {
            display: block;
        }
    }

    @media screen and (max-width: 1366px) {
        row-gap: 10px;
    }
`;

const CircleCheckIcon = styled(CheckCircle)`
    color: ${theme.app.secondary};
    font-size: 20px;
`;

const CircleIcon = styled(Circle)`
    color: ${theme.app.disabledOpacity};
    font-size: 20px;
`;

const CheckListIcon = styled(ListCheck)`
    font-size: 25px;
    margin-right: 10px;
`;

interface ToDoListProps {
    hasStoreConnected: boolean;
    hasFirstCustomizableProduct: boolean;
    hasPlaceTestOrder: boolean;
    userId: number;
}

const ToDoList: FunctionComponent<ToDoListProps> = (props) => {
    // State
    const [open, setOpen] = useState(localStorage?.getItem('ZakTimelineOpened_' + props.userId) == null);
    const [understandingZakekeChecked, setUnderstandingZakekeChecked] = useState(
        localStorage?.getItem('UnderstandingZakekeClickedTimeLine_' + props.userId) != null
    );

    useEffect(() => {
        try {
            if (!localStorage?.getItem('ZakTimelineOpened_' + props.userId)) {
                localStorage.setItem('ZakTimelineOpened_' + props.userId, 'TRUE');
            }
        } catch (ex) {
            console.warn('Localstorage not supported');
        }
    }, []);

    let numTaskCompleted = 2;
    props.hasFirstCustomizableProduct && numTaskCompleted++;
    props.hasStoreConnected && numTaskCompleted++;
    props.hasPlaceTestOrder && numTaskCompleted++;
    understandingZakekeChecked && numTaskCompleted++;

    return numTaskCompleted < 6 ? (
        <React.Fragment>
            <ButtonTodoList onClick={() => setOpen(!open)}>
                <Icon>
                    <CheckListIcon></CheckListIcon>
                </Icon>
                <label>{T._('To do list', 'Admin')}</label>
            </ButtonTodoList>
            <ContainerToDoList className={open ? 'open' : ''}>
                <CloseButton onClick={() => setOpen(!open)}>
                    <Icon>
                        <Close></Close>
                    </Icon>
                </CloseButton>
                <Title>{T._('Your next steps', 'Admin')}</Title>
                <SubTitle>Set up your Zakeke account by following these simple steps</SubTitle>
                <NumberStepsRemaining>
                    <label>
                        {numTaskCompleted} {T._('of', 'Admin')} 6{' '}
                    </label>
                    <label>{T._('steps completed', 'Admin')}</label>
                </NumberStepsRemaining>
                <TicksSteps>
                    <Tick $completed={numTaskCompleted >= 1}></Tick>
                    <Tick $completed={numTaskCompleted >= 2}></Tick>
                    <Tick $completed={numTaskCompleted >= 3}></Tick>
                    <Tick $completed={numTaskCompleted >= 4}></Tick>
                    <Tick $completed={numTaskCompleted >= 5}></Tick>
                    <Tick $completed={numTaskCompleted == 6}></Tick>
                </TicksSteps>
                <Steps>
                    <Step completed={true} text={T._('Signup', 'Admin')}></Step>
                    <Step completed={true} text={T._('Start free trial', 'Admin')}></Step>
                    <Step
                        completed={understandingZakekeChecked}
                        text={T._('Understanding Zakeke', 'Admin')}
                        linkInfoUrl={'https://zakeke.zendesk.com/hc/en-us/articles/360021604400-Understanding-Zakeke'}
                        onLinkClickCallback={() => {
                            setUnderstandingZakekeChecked(true);
                            if (localStorage && !localStorage?.getItem('UnderstandingZakekeClickedTimeLine_' + props.userId)) {
                                localStorage.setItem('UnderstandingZakekeClickedTimeLine_' + props.userId, 'TRUE');
                            }
                        }}
                    ></Step>
                    <Step
                        completed={props.hasFirstCustomizableProduct}
                        text={T._('Create your first customizable product', 'Admin')}
                        linkInfoUrl={'https://zakeke.zendesk.com/hc/en-us/articles/6396920276124'}
                    ></Step>
                    <Step
                        completed={props.hasStoreConnected}
                        text={T._('Connect your eStore', 'Admin')}
                        linkInfoUrl={'https://zakeke.zendesk.com/hc/en-us/articles/360026666973-Installing-Zakeke'}
                    ></Step>
                    <Step
                        completed={props.hasPlaceTestOrder}
                        text={T._('Place a test order', 'Admin')}
                        linkInfoUrl={'https://zakeke.zendesk.com/hc/en-us/articles/360018551700-How-to-test-a-customizable-product-and-place-a-test-order'}
                    ></Step>
                </Steps>
            </ContainerToDoList>
        </React.Fragment>
    ) : null;
};

const Step: FunctionComponent<{ completed: boolean; text: string; linkInfoUrl?: string; onLinkClickCallback?: any }> = (props) => {
    return (
        <li>
            <Icon>{props.completed ? <CircleCheckIcon /> : <CircleIcon />}</Icon>
            <div>
                <label>{props.completed ? <s>{props.text}</s> : props.text}</label>
                {props.linkInfoUrl && (
                    <Link href={props.linkInfoUrl} target='_blank' onClick={props.onLinkClickCallback}>
                        {T._('Learn more', 'Admin')}
                    </Link>
                )}
            </div>
        </li>
    );
};

export default ToDoList;
